// Sign-in, sign-up, forgotten password, reset and e-mail confirmation (US-001, US-002, T-106).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, problemText } from "../api";
import { Button, ErrorText, Field, Logo } from "../components/ui";
import { hasMessage, locale, t, type MessageKey } from "../i18n";
import { LanguageSelect } from "../components/LanguageSelect";

function AuthFrame({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return (
    <div className="grid min-h-full place-items-center px-4 py-12">
      <div className="flex w-full max-w-[400px] flex-col gap-6">
        <Logo className="h-7" />
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 text-2xl font-semibold tracking-tight">{title}</h1>
          {lead && <p className="m-0 text-steel">{lead}</p>}
        </div>
        {children}
        <LanguageSelect signedIn={false} className="self-start" />
      </div>
    </div>
  );
}


type SignInResult = { status: "signed_in" } | { status: "mfa_required"; challenge: string };

// Once the person is known, the sign-in route forwards to `next` itself (AfterSignIn in
// main.tsx). Navigating here as well raced the update of "me": the signed-out routes sent the
// target page back to /sign-in without `next`, which ended on Home.
function useFinishSignIn() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
  };
}

// The second step of a sign-in (T-106): a code from the authenticator app or a recovery code.
function SecondStep({ challenge }: { challenge: string }) {
  const finish = useFinishSignIn();
  const [code, setCode] = useState("");
  const verify = useMutation({
    mutationFn: () => api.post<SignInResult>("/auth/sign-in/mfa", { challenge, code }),
    onSuccess: finish,
  });
  return (
    <AuthFrame title={t("mfa.step.title")} lead={t("mfa.step.lead")}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          verify.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Field id="code" autoComplete="one-time-code" inputMode="text" required autoFocus label={t("mfa.field.code")} hint={t("mfa.field.codeHint")} value={code} onChange={(e) => setCode(e.target.value)} />
        {verify.isError && <ErrorText>{problemText(verify.error)}</ErrorText>}
        <Button kind="primary" size="lg" type="submit" disabled={verify.isPending}>
          {verify.isPending ? t("common.working") : t("mfa.step.submit")}
        </Button>
      </form>
      <Link to="/sign-in" reloadDocument className="text-sm text-blue no-underline hover:underline">
        {t("auth.forgot.back")}
      </Link>
    </AuthFrame>
  );
}

// A company's own sign-in (T-421): FRAME60 finds it by the address's verified domain and sends
// the browser to the identity provider.
function SingleSignOn({ onBack }: { onBack: () => void }) {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const required = params.get("sso") === "required";
  const message = (() => {
    try {
      return sessionStorage.getItem("f60.ssoRequired");
    } catch {
      return null;
    }
  })();
  const error = params.get("sso_error");
  const start = useMutation({
    mutationFn: () => api.post<{ redirect: string }>("/auth/sso/start", { email }),
    onSuccess: (started) => window.location.assign(started.redirect),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        start.mutate();
      }}
      className="flex flex-col gap-4"
    >
      {required && <p className="m-0 rounded-[7px] border border-rule bg-panel px-3 py-2 text-sm">{message || t("sso.required")}</p>}
      {error && <ErrorText>{hasMessage(`error.${error}`) ? t(`error.${error}` as MessageKey) : t("error.sso_failed")}</ErrorText>}
      <p className="m-0 text-sm text-steel">{t("sso.lead")}</p>
      <Field id="sso-email" type="email" autoComplete="email" required label={t("auth.field.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
      {start.isError && <ErrorText>{problemText(start.error)}</ErrorText>}
      <Button kind="primary" size="lg" type="submit" disabled={start.isPending}>
        {start.isPending ? t("common.working") : t("sso.submit")}
      </Button>
      <button type="button" className="self-start border-0 bg-transparent p-0 text-sm text-blue hover:underline" onClick={onBack}>
        {t("magic.usePassword")}
      </button>
    </form>
  );
}

export function SignIn() {
  const finish = useFinishSignIn();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"password" | "link" | "sso">(params.get("sso") || params.get("sso_error") ? "sso" : "password");
  const [challenge, setChallenge] = useState<string | null>(null);
  const signIn = useMutation({
    mutationFn: () => api.post<SignInResult>("/auth/sign-in", { email, password }),
    onSuccess: async (result) => {
      if (result.status === "mfa_required") setChallenge(result.challenge);
      else await finish();
    },
  });
  const link = useMutation({ mutationFn: () => api.post("/auth/magic-link", { email }) });
  if (challenge) return <SecondStep challenge={challenge} />;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (mode === "password") signIn.mutate();
    else link.mutate();
  };
  return (
    <AuthFrame title={t("auth.signIn.title")}>
      {mode === "sso" ? (
        <SingleSignOn onBack={() => setMode("password")} />
      ) : link.isSuccess ? (
        <p className="m-0 rounded-[7px] border border-rule bg-panel px-3 py-2">{t("magic.sent")}</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field id="email" type="email" autoComplete="email" required label={t("auth.field.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
          {mode === "password" && (
            <Field id="password" type="password" autoComplete="current-password" required label={t("auth.field.password")} value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
          {signIn.isError && <ErrorText>{problemText(signIn.error)}</ErrorText>}
          {link.isError && <ErrorText>{problemText(link.error)}</ErrorText>}
          <Button kind="primary" size="lg" type="submit" disabled={signIn.isPending || link.isPending}>
            {signIn.isPending || link.isPending ? t("common.working") : mode === "password" ? t("auth.signIn.submit") : t("magic.submit")}
          </Button>
          <button type="button" className="self-start border-0 bg-transparent p-0 text-sm text-blue hover:underline" onClick={() => setMode(mode === "password" ? "link" : "password")}>
            {mode === "password" ? t("magic.instead") : t("magic.usePassword")}
          </button>
          <button type="button" className="self-start border-0 bg-transparent p-0 text-sm text-blue hover:underline" onClick={() => setMode("sso")}>
            {t("sso.signIn")}
          </button>
        </form>
      )}
      <div className="flex flex-col gap-2 text-sm">
        <Link to="/forgot-password" className="text-blue no-underline hover:underline">
          {t("auth.signIn.forgot")}
        </Link>
        <span className="text-steel">
          {t("auth.signIn.noAccount")}{" "}
          <Link to={`/sign-up${window.location.search}`} className="text-blue no-underline hover:underline">
            {t("auth.signIn.createAccount")}
          </Link>
        </span>
      </div>
    </AuthFrame>
  );
}

// The page a magic link opens: signs in, or asks for the second step.
export function MagicLink() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const finish = useFinishSignIn();
  const [challenge, setChallenge] = useState<string | null>(null);
  const consume = useMutation({
    mutationFn: () => api.post<SignInResult>("/auth/magic-link/consume", { token }),
    onSuccess: async (result) => {
      if (result.status === "mfa_required") setChallenge(result.challenge);
      else await finish();
    },
  });
  const { mutate, isIdle } = consume;
  useEffect(() => {
    if (token && isIdle) mutate();
  }, [token, isIdle, mutate]);
  if (challenge) return <SecondStep challenge={challenge} />;
  return (
    <AuthFrame title={t("magic.title")}>
      {!token && <ErrorText>{t("auth.missingToken")}</ErrorText>}
      {consume.isPending && <p className="m-0 text-steel">{t("common.working")}</p>}
      {consume.isError && (
        <>
          <ErrorText>{problemText(consume.error)}</ErrorText>
          <Link to="/sign-in" className="text-sm text-blue no-underline hover:underline">
            {t("auth.forgot.back")}
          </Link>
        </>
      )}
    </AuthFrame>
  );
}

export function SignUp() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const signUp = useMutation({
    mutationFn: () => api.post("/auth/sign-up", { ...form, locale: locale() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : "/", { replace: true });
    },
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });
  return (
    <AuthFrame title={t("auth.signUp.title")} lead={t("auth.signUp.lead")}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          signUp.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Field id="name" autoComplete="name" required label={t("auth.field.name")} value={form.name} onChange={set("name")} />
        <Field id="company" autoComplete="organization" required label={t("auth.field.company")} value={form.company} onChange={set("company")} />
        <Field id="email" type="email" autoComplete="email" required label={t("auth.field.email")} value={form.email} onChange={set("email")} />
        <Field id="password" type="password" autoComplete="new-password" required minLength={10} label={t("auth.field.password")} hint={t("auth.field.passwordHint")} value={form.password} onChange={set("password")} />
        {signUp.isError && <ErrorText>{problemText(signUp.error)}</ErrorText>}
        <Button kind="primary" size="lg" type="submit" disabled={signUp.isPending}>
          {signUp.isPending ? t("common.working") : t("auth.signUp.submit")}
        </Button>
      </form>
      <span className="text-sm text-steel">
        {t("auth.signUp.haveAccount")}{" "}
        <Link to="/sign-in" className="text-blue no-underline hover:underline">
          {t("auth.signIn.submit")}
        </Link>
      </span>
    </AuthFrame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const forgot = useMutation({ mutationFn: () => api.post("/auth/password/forgot", { email }) });
  return (
    <AuthFrame title={t("auth.forgot.title")} lead={t("auth.forgot.lead")}>
      {forgot.isSuccess ? (
        <p className="m-0 rounded-[7px] border border-rule bg-panel px-3 py-2">{t("auth.forgot.sent")}</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            forgot.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field id="email" type="email" autoComplete="email" required label={t("auth.field.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
          {forgot.isError && <ErrorText>{problemText(forgot.error)}</ErrorText>}
          <Button kind="primary" size="lg" type="submit" disabled={forgot.isPending}>
            {forgot.isPending ? t("common.working") : t("auth.forgot.submit")}
          </Button>
        </form>
      )}
      <Link to="/sign-in" className="text-sm text-blue no-underline hover:underline">
        {t("auth.forgot.back")}
      </Link>
    </AuthFrame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const reset = useMutation({ mutationFn: () => api.post("/auth/password/reset", { token, password }) });
  return (
    <AuthFrame title={t("auth.reset.title")}>
      {!token ? (
        <ErrorText>{t("auth.missingToken")}</ErrorText>
      ) : reset.isSuccess ? (
        <>
          <p className="m-0 rounded-[7px] border border-rule bg-panel px-3 py-2">{t("auth.reset.done")}</p>
          <Link to="/sign-in" className="text-sm text-blue no-underline hover:underline">
            {t("auth.signIn.submit")}
          </Link>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reset.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field id="password" type="password" autoComplete="new-password" required minLength={10} label={t("auth.field.password")} hint={t("auth.field.passwordHint")} value={password} onChange={(e) => setPassword(e.target.value)} />
          {reset.isError && <ErrorText>{problemText(reset.error)}</ErrorText>}
          <Button kind="primary" size="lg" type="submit" disabled={reset.isPending}>
            {reset.isPending ? t("common.working") : t("auth.reset.submit")}
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const queryClient = useQueryClient();
  const verify = useMutation({
    mutationFn: () => api.post("/auth/email/verify", { token }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
  const { mutate, isIdle } = verify;
  useEffect(() => {
    if (token && isIdle) mutate();
  }, [token, isIdle, mutate]);
  return (
    <AuthFrame title={t("auth.verify.title")}>
      {!token && <ErrorText>{t("auth.missingToken")}</ErrorText>}
      {verify.isPending && <p className="m-0 text-steel">{t("common.working")}</p>}
      {verify.isError && <ErrorText>{problemText(verify.error)}</ErrorText>}
      {verify.isSuccess && (
        <>
          <p className="m-0 rounded-[7px] border border-rule bg-panel px-3 py-2">{t("auth.verify.done")}</p>
          <Link to="/" className="text-sm text-blue no-underline hover:underline">
            {t("auth.verify.continue")}
          </Link>
        </>
      )}
    </AuthFrame>
  );
}

interface InvitationInfo {
  email: string;
  role: string;
  company_name: string;
  invited_by_name: string | null;
  has_account: boolean;
}

/// The page an invitation e-mail opens (T-420): join the company, creating an account first
/// when the address has none, or signing in to the matching account.
export function Invitation({ me }: { me: { email: string } | null }) {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", password: "" });
  const info = useMutation({ mutationFn: () => api.post<InvitationInfo>("/auth/invitation", { token }) });
  const lookup = info.mutate;
  useEffect(() => {
    if (token) lookup();
  }, [token, lookup]);
  const done = async () => {
    await queryClient.invalidateQueries();
    navigate("/", { replace: true });
  };
  const accept = useMutation({ mutationFn: () => api.post("/auth/invitation/accept", { token }), onSuccess: done });
  const signUp = useMutation({
    mutationFn: () => api.post("/auth/sign-up", { email: info.data?.email, name: form.name, password: form.password, invitation: token, locale: locale() }),
    onSuccess: done,
  });
  if (!token) {
    return (
      <AuthFrame title={t("auth.signIn.title")}>
        <ErrorText>{t("inv.missing")}</ErrorText>
      </AuthFrame>
    );
  }
  if (info.isError) {
    return (
      <AuthFrame title={t("auth.signIn.title")}>
        <ErrorText>{problemText(info.error)}</ErrorText>
      </AuthFrame>
    );
  }
  const i = info.data;
  if (!i) return <div className="grid min-h-full place-items-center text-steel">{t("common.working")}</div>;
  const role = t(`role.${i.role}` as Parameters<typeof t>[0]);
  const lead = i.invited_by_name
    ? t("inv.lead", { inviter: i.invited_by_name, company: i.company_name, role })
    : t("inv.leadNoInviter", { company: i.company_name, role });
  const next = `/invitation?token=${encodeURIComponent(token)}`;
  return (
    <AuthFrame title={t("inv.title", { company: i.company_name })} lead={lead}>
      {me ? (
        me.email.toLowerCase() === i.email.toLowerCase() ? (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-sm text-steel">{t("inv.signedInAs", { email: me.email })}</p>
            {accept.isError && <ErrorText>{problemText(accept.error)}</ErrorText>}
            <Button kind="primary" size="lg" disabled={accept.isPending} onClick={() => accept.mutate()}>
              {accept.isPending ? t("common.working") : t("inv.accept")}
            </Button>
          </div>
        ) : (
          <ErrorText>{t("inv.otherAccount", { email: i.email })}</ErrorText>
        )
      ) : i.has_account ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-steel">{t("inv.signIn", { email: i.email })}</p>
          <Link to={`/sign-in?next=${encodeURIComponent(next)}`} className="no-underline">
            <Button kind="primary" size="lg" className="w-full">
              {t("auth.signIn.submit")}
            </Button>
          </Link>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            signUp.mutate();
          }}
        >
          <p className="m-0 text-sm text-steel">{t("inv.create", { email: i.email })}</p>
          <Field id="name" autoComplete="name" required label={t("auth.field.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            label={t("auth.field.password")}
            hint={t("auth.field.passwordHint")}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          {signUp.isError && <ErrorText>{problemText(signUp.error)}</ErrorText>}
          <Button kind="primary" size="lg" type="submit" disabled={signUp.isPending}>
            {signUp.isPending ? t("common.working") : t("inv.createSubmit")}
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
